import {
  Component,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy, Self
} from "@angular/core";
import { IHeaderAngularComp } from "ag-grid-angular";
import { IHeaderParams } from "ag-grid-community";
import get from "lodash/get";
import omit from "lodash/omit";
import { BsshDataTableMessageService } from "../services/bssh-data-table-message.service";
import { CustomColDef } from "../custom-colDef";

@Component({
  selector: "lib-data-table-custom-header",
  templateUrl: "custom-header.component.html",
  styleUrls: ["custom-header.component.scss"]
})
export class CustomHeaderComponent
  implements IHeaderAngularComp, AfterViewInit, OnDestroy {
  @ViewChild("menuButton", { read: ElementRef, static: true })
  public menuButton;
  public params: ICustomHeaderParams;
  public activeSort: string;
  public activeFilter: boolean;
  public headerName: string;
  public colDef: CustomColDef;
  private messageServiceSubscription: any;
  public overflow: boolean;

  constructor(private messageService: BsshDataTableMessageService, @Self() private element: ElementRef) { }

  get hasFilter(): boolean {
    return this.colDef && (this.colDef.filterActive || this.colDef.dateRangeFilterActive || this.colDef.numberRangeFilterActive);
  }

  public agInit(params: ICustomHeaderParams): void {
    this.params = params;

    this.colDef = params.column.getColDef();

    this.setActiveSort();

    const filterValue = get(this.params.api.getFilterModel(), `${this.params.column.getColId()}.value`);

    this.headerName = filterValue || this.params.displayName;

    this.setActiveFilter();
  }

  ngAfterViewInit() {
    this.messageServiceSubscription = this.messageService
      .getMessage()
      .subscribe(({ text, data }) => {
        if (text === "headerNameUpdated") {
          const colId = this.params.column.getColId();
          const filterModel = this.params.api.getFilterModel();
          const filterValue = get(filterModel, `${colId}.displayName`);
          const filterFromValue = get(filterModel, `${colId}.fromValue`);
          const filterToValue = get(filterModel, `${colId}.toValue`);

          if (filterValue) { // if filter value option set
            this.headerName = filterValue;
          } else if (filterFromValue && filterToValue) { // if filter date range or number range set
            // Check if it's a Date object (date range) or number (number range)
            if (filterFromValue instanceof Date) {
              this.headerName = `${this.formatDate(filterFromValue)} - ${this.formatDate(filterToValue)}`;
            } else {
              this.headerName = `${filterFromValue} - ${filterToValue}`;
            }
          } else if (filterFromValue) { // Only min value set
            this.headerName = `≥ ${filterFromValue}`;
          } else if (filterToValue) { // Only max value set
            this.headerName = `≤ ${filterToValue}`;
          } else {
            this.headerName = this.params.displayName;
          }
        }

        this.setActiveFilter();

        if (text === "orderingUpdated") {
          this.setActiveSort();
        }
        // Default overflow to false first
        this.overflow = false;
        // Wait until event cycle finishes to detect if overflow occurs
        setTimeout(() => {
          // Get the title header
          // Compare scroll width and offset width to check if text overflowed
          const headerSpan = this.element.nativeElement.querySelector("span.ag-header-cell-text__title");
          if (headerSpan) {
            this.overflow = headerSpan.scrollWidth > headerSpan.offsetWidth;
          }
        }, 0);
      });
  }

  public onMenuClicked(event) {
    if (this.colDef.sortable === true || this.hasFilter) {
      this.params.showColumnMenu(this.menuButton.nativeElement);
    }
  }

  public onSortRequest(order, event) {
    // enforce single column sorting only
    const newSortModel = [
      {
        colId: this.params.column.getColId(),
        sort: order
      }
    ];
    this.params.api.setSortModel(newSortModel);

    this.setActiveSort();
  }

  public onDropFiltersSort(event) {
    // only clear either sort or filter model to prevent 2 api calls
    const filterModel = this.params.api.getFilterModel();
    if (get(filterModel, this.params.column.getColId())) {
      // clear current filter
      const updatedFilterModel = omit(filterModel, this.params.column.getColId());
      this.params.api.setFilterModel(updatedFilterModel);
    } else if (this.activeSort) {
      // clear sort
      this.params.api.setSortModel(null);
    }
    this.headerName = this.params.displayName;
    this.setActiveSort();
    this.setActiveFilter();
  }

  ngOnDestroy() {
    if (
      this.messageServiceSubscription &&
      typeof this.messageServiceSubscription.unsubscribe === "function"
    ) {
      this.messageServiceSubscription.unsubscribe();
    }
  }

  private setActiveSort() {
    this.activeSort = this.params.column.getSort();
  }

  private setActiveFilter() {
    this.activeFilter = this.headerName !== this.params.displayName;
  }

  // Formats date as string YYYY-MM-DD
  private formatDate(date: Date) {
    let month = "" + (date.getMonth() + 1);
    let day = "" + date.getDate();
    const  year = date.getFullYear();

    if (month.length < 2) {
      month = "0" + month;
    }

    if (day.length < 2) {
      day = "0" + day;
    }

    return [year, month, day].join("-");
  }
}

export interface ICustomHeaderParams extends IHeaderParams {
  infoPopoverContent?: string; // content of optional info popover, if null it won't be rendered
}
